import { createApiCall } from 'services'
import { getErrorMessage } from 'utils'


import type {
  GetAnalyticsTotalsPayload,
  GetAnalyticsTotalsResponse,
  GetAnalyticsStatisticsPayload,
  GetAnalyticsStatisticsResponse,
  GetAnalyticsScansMapPayload,
  GetAnalyticsScansMapResponse,
  GetAnalyticsRoiPayload,
  GetAnalyticsRoiResponse,
} from './types'
import type { ActionCallback } from '../model'

import { call, put, fork, takeEvery, takeLatest } from 'redux-saga/effects'

import { Actions, analyticsScansMapKey } from './types'





export function* getAnalyticsTotalsSaga(): Generator {
  yield takeLatest(Actions.GET_ANALYTICS_TOTALS, function* (action: ActionCallback<GetAnalyticsTotalsPayload>) {
    try {
      const { payload, callback } = action

      const response: GetAnalyticsTotalsResponse = yield call(createApiCall, {
        url: `/analytics/admin/total`,
        params: payload,
      })

      const { data, status } = response

      if (status === 200) {
        yield put({
          type: Actions.SET_ANALYTICS_TOTALS,
          payload: data,
        })
      }

      callback && callback({ success: true, content: data })
    } catch (e) {
      console.error(e)
      action.callback && action.callback({ success: false, content: getErrorMessage(e) })
    }
  })
}

export function* getAnalyticsStatisticsSaga(): Generator {
  yield takeLatest(Actions.GET_ANALYTICS_STATISTICS, function* (action: ActionCallback<GetAnalyticsStatisticsPayload>) {
    try {
      const { payload, callback } = action

      const response: GetAnalyticsStatisticsResponse = yield call(createApiCall, {
        url: `/analytics/admin/granular`,
        params: payload,
      })

      const { data, status } = response

      if (status === 200) {
        yield put({
          type: Actions.SET_ANALYTICS_STATISTICS,
          payload: data,
        })
      }

      callback && callback({ success: true, content: data })
    } catch (e) {
      console.error(e)
      action.callback && action.callback({ success: false, content: getErrorMessage(e) })
    }
  })
}

export function* getAnalyticsScansMapSaga(): Generator {
  /*
    ⚠️ `takeEvery`, NOT `takeLatest`, and the difference is a correctness fix rather than a
    tuning choice — see `AnalyticsScansMapCache` for the whole argument.

    `takeLatest` cancels the in-flight request when a second one starts. That is right when
    there is ONE consumer asking successive versions of one question, and wrong the moment
    two different cards ask two different questions at mount: the first card's request is
    cancelled and it never receives an answer at all.

    Two requests for the SAME key are still safe to let both land — identical params give
    identical results, so an out-of-order arrival cannot write anything stale.
  */
  yield takeEvery(Actions.GET_ANALYTICS_SCANS_MAP, function* (action: ActionCallback<GetAnalyticsScansMapPayload>) {
    try {
      const { payload, callback } = action

      const response: GetAnalyticsScansMapResponse = yield call(createApiCall, {
        url: `/analytics/admin/scans-map`,
        params: payload,
      })

      const { data, status } = response

      if (status === 200) {
        yield put({
          type: Actions.SET_ANALYTICS_SCANS_MAP,
          // The key is computed from the REQUEST payload, never from `data.query` the
          // server echoes back: that round-trips through JSON and returns dates as
          // strings, so it would never match a key the client computed from `Date`s.
          payload: { key: analyticsScansMapKey(payload), response: data },
        })
      }

      callback && callback({ success: true, content: data })
    } catch (e) {
      console.error(e)
      action.callback && action.callback({ success: false, content: getErrorMessage(e) })
    }
  })
}

export function* getAnalyticsRoiSaga(): Generator {
  yield takeLatest(Actions.GET_ANALYTICS_ROI, function* (action: ActionCallback<GetAnalyticsRoiPayload>) {
    try {
      const { payload, callback } = action

      const response: GetAnalyticsRoiResponse = yield call(createApiCall, {
        url: `/analytics/admin/roi`,
        params: payload,
      })

      const { data, status } = response

      if (status === 200) {
        yield put({
          type: Actions.SET_ANALYTICS_ROI,
          payload: data,
        })
      }

      callback && callback({ success: true, content: data })
    } catch (e) {
      console.error(e)
      action.callback && action.callback({ success: false, content: getErrorMessage(e) })
    }
  })
}

/**
 * The HERO's ROI request — same endpoint, its OWN `takeLatest` channel and its own slot.
 *
 * Deliberately a duplicate of `getAnalyticsRoiSaga` rather than a shared generator
 * parameterised by action type. `takeLatest` cancels the previous run OF ITS OWN PATTERN,
 * which is exactly the isolation being bought here: a hero request must never cancel a
 * cards request measured over a different window, and vice versa. A shared implementation
 * would have to take both the watched action AND the action to dispatch as arguments,
 * which is more indirection than the eight duplicated lines it removes — and it would put
 * the one property that matters (two independent channels) behind an argument.
 */
export function* getAnalyticsRoiHeroSaga(): Generator {
  yield takeLatest(Actions.GET_ANALYTICS_ROI_HERO, function* (action: ActionCallback<GetAnalyticsRoiPayload>) {
    try {
      const { payload, callback } = action

      const response: GetAnalyticsRoiResponse = yield call(createApiCall, {
        url: `/analytics/admin/roi`,
        params: payload,
      })

      const { data, status } = response

      if (status === 200) {
        yield put({
          type: Actions.SET_ANALYTICS_ROI_HERO,
          payload: data,
        })
      }

      callback && callback({ success: true, content: data })
    } catch (e) {
      console.error(e)
      action.callback && action.callback({ success: false, content: getErrorMessage(e) })
    }
  })
}

export default [
  fork(getAnalyticsTotalsSaga),
  fork(getAnalyticsStatisticsSaga),
  fork(getAnalyticsScansMapSaga),
  fork(getAnalyticsRoiSaga),
  fork(getAnalyticsRoiHeroSaga),
]
